import React, { ChangeEvent, useEffect, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useLocation } from '@reach/router';
import { useFormikContext, FormikValues } from 'formik';
import moment, { Moment } from 'moment';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { InputDescription, InputLabel } from '@components/shared/Typography';
import { useCountry } from '@hooks/useCountry';
import { parseDateFromString } from '@utils/date';
import { replaceText } from '@utils/handleTextReplacement';
import { sanitizeHtml } from '@utils/html';
import FormFieldError from './FormFieldError';

interface Props {
  id: string;
  name: string;
  label?: string;
  description?: string;
  error?: any;
  value?: string;
  onChange?: (e: ChangeEvent<any>) => void;
  openTo?: 'year' | 'month' | 'day';
  disableFuture?: boolean;
  disablePast?: boolean;
  shouldDisableDate?: (day: Moment) => boolean;
}

const DateInput: React.FC<Props> = ({
  label,
  description,
  name,
  value,
  openTo,
  disableFuture = true,
  disablePast = false,
  shouldDisableDate = () => false
}) => {
  const { country } = useCountry();
  const location = useLocation();
  const { setFieldValue, setFieldTouched, errors, touched } = useFormikContext<FormikValues>();
  const initialValue = value ? parseDateFromString(value, 'dd/mm/yyyy') : null;
  const [dateValue, setDateValue] = useState<Moment | null>(initialValue ? moment(initialValue) : null);
  const hasError = Boolean(errors[name] && touched[name]);

  useEffect(() => {
    setFieldTouched(name, true, false);
    if (!dateValue) {
      setFieldValue(name, '', true);
      return;
    }

    // Safely check if dateValue is a valid moment object before using isValid
    if (moment.isMoment(dateValue) && dateValue.isValid()) {
      const newDateString = dateValue.format('DD/MM/YYYY');
      setFieldValue(name, newDateString, true);
    } else {
      setFieldValue(name, '', true);
    }
  }, [dateValue, name, setFieldTouched, setFieldValue]);

  return (
    <InputWrapper>
      {label && <InputLabel>{replaceText(label, country, location)}</InputLabel>}
      {description && <InputDescription dangerouslySetInnerHTML={{ __html: sanitizeHtml(replaceText(description, country, location)) }} />}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <StyledDatePicker
          disableFuture={disableFuture}
          disablePast={disablePast}
          openTo={openTo ?? 'year'}
          views={['year', 'month', 'day']}
          value={dateValue}
          onChange={(newValue) => setDateValue(newValue)}
          format="DD/MM/YYYY"
          shouldDisableDate={shouldDisableDate}
          slotProps={{
            textField: {
              placeholder: 'Choose a date (dd/mm/yyyy)'
            }
          }}
        />
      </LocalizationProvider>
      {hasError && <FormFieldError message={String(errors[name])} style={{ marginTop: 10 }} />}
    </InputWrapper>
  );
};

export default DateInput;

const InputWrapper = styled.div`
  position: relative;
  margin-bottom: 40px;
  color: ${Colors.navyBlue};
`;

const StyledDatePicker = styled(DatePicker)`
  background: ${Colors.cream};
  width: 100%;

  & input {
    width: 100%;
    font-family: 'BrownStd';
    font-size: 15px;
    line-height: 24px;
    color: ${Colors.navyBlue};
  }

  & label.Mui-focused {
    color: white;
  }

  & .MuiInput-underline:after {
    border-bottom-color: ${Colors.paleGold};
  }
  & .MuiOutlinedInput-root {
    & fieldset {
      border-color: ${Colors.paleGold};
      border-radius: 2px;
    }
    &:hover fieldset {
      border-color: ${Colors.paleGold};
    }
    &.Mui-focused fieldset {
      border-color: ${Colors.paleGold};
    }
  }
`;
